// Подбадривающие сообщения менеджеру. Правила: показывать в ответ на сделанную работу,
// не чаще раза в час, не больше шести за смену, не повторяться и не перебивать ввод.
// Всё хранится в браузере — на сервер ничего не уходит.

export interface DayStats {
  assembled: number; assembled_prev: number; shipped: number;
  units: number; receipts: number; picks: number; waiting: number;
}
export interface Phrase { id: string; text: string }

export const MIN_GAP_MS = 60 * 60 * 1000;   // пауза между любыми сообщениями
export const MAX_PER_DAY = 6;               // потолок за смену
export const IDLE_GAP_MS = 100 * 60 * 1000; // фоновая фраза — только если давно ничего не было
export const SHOW_MS = 6000;                // сколько плашка висит на экране

const plural = (n: number, one: string, few: string, many: string) => {
  const a = Math.abs(n) % 100, b = a % 10;
  if (a > 10 && a < 20) return many;
  if (b > 1 && b < 5) return few;
  return b === 1 ? one : many;
};
export const orders = (n: number) => `${n} ${plural(n, 'заказ', 'заказа', 'заказов')}`;
export const items = (n: number) => `${n} ${plural(n, 'позиция', 'позиции', 'позиций')}`;

/** Фразы на событие: срабатывают, когда счётчик за смену вырос */
const EVENT: Record<string, (s: DayStats, d: number) => string[]> = {
  assembled: (s, d) => [
    `Собрано ${orders(s.assembled)} за сегодня. Ровно идём.`,
    `Плюс ${orders(d)} в сборке — итого ${s.assembled} за смену.`,
    `${s.assembled} собрано, ${items(s.units)} прошло через ваши руки.`,
    ...(s.assembled > s.assembled_prev && s.assembled_prev > 0
      ? [`${s.assembled} заказов — на ${s.assembled - s.assembled_prev} больше, чем вчера к этому часу.`,
         `Сегодня идёте быстрее вчерашнего: ${s.assembled} против ${s.assembled_prev}.`]
      : []),
    ...(s.waiting === 0 ? [`Очередь на сборку пуста — всё разобрано.`] : []),
    ...(s.assembled >= 50 ? [`${s.assembled} заказов за смену — это много. Хорошая работа.`] : []),
  ],
  shipped: (s, d) => [
    `Отгружено ${orders(s.shipped)} — они уже едут к покупателям.`,
    `Партия ушла: плюс ${orders(d)} в отгрузке.`,
    `${s.shipped} заказов сегодня доехало до перевозчика. Дело сделано.`,
  ],
  receipts: (s) => [
    `Приёмка проведена — товар на месте, остатки сходятся.`,
    `${s.receipts === 1 ? 'Первая приёмка за день закрыта' : `Закрыто приёмок за день: ${s.receipts}`}. Склад пополнен.`,
    `Приход оформлен. Спасибо за аккуратность — от неё зависят все остатки.`,
  ],
  picks: (s) => [
    `Лист подбора закрыт. ${s.picks === 1 ? 'Первый за смену' : `Всего за смену: ${s.picks}`}.`,
    `Подбор завершён — заказы готовы к упаковке.`,
    `Ещё один лист позади. Порядок в зоне сборки держится на этом.`,
  ],
};

/** Фоновые фразы: без цифр, спокойные, показываются редко */
const IDLE = [
  'Ровный ритм — половина дела. Вы его держите.',
  'День идёт своим ходом. Не забудьте про перерыв.',
  'Сложный день не делает работу плохой. Он просто сложный.',
  'Каждая правильная строка в остатках — это чей-то вовремя полученный заказ.',
  'Точность важнее скорости, и у вас с ней порядок.',
  'Хорошо, когда склад предсказуем. Это не случайность, а ваша работа.',
  'Если что-то не сошлось — это повод разобраться, а не повод расстраиваться.',
  'Спокойная смена — тоже результат.',
  'Разобранная очередь заказов начинается с одного собранного.',
  'Немного воды и пара минут от экрана — и дальше пойдёт легче.',
  'То, что вы делаете руками, потом видно в цифрах месяца.',
  'Ошибка, которую заметили сами, уже почти не ошибка.',
  'Порядок в ячейках сегодня — сэкономленный час завтра.',
  'Вы знаете этот склад лучше любой программы. Программа просто помогает.',
  'Пусть остаток сойдётся с первого раза.',
  'Хорошая смена складывается из обычных, ничем не примечательных действий.',
  'Если устали — это нормально. Работа тяжёлая.',
  'Маленькие улучшения в процессе дают больше, чем один героический рывок.',
  'Сегодня тоже кто-то получит свою посылку вовремя — благодаря вам.',
  'Спасибо, что доводите документы до конца, а не до середины.',
];

/** Уже показанные фразы не повторяем, пока не кончится запас */
export function pickPhrase(pool: Phrase[], recent: string[], rnd: () => number = Math.random): Phrase | null {
  if (!pool.length) return null;
  const fresh = pool.filter((p) => !recent.includes(p.id));
  const from = fresh.length ? fresh : pool;
  return from[Math.floor(rnd() * from.length)] ?? null;
}

/** Что изменилось со времени прошлой проверки — по этому выбираем группу фраз */
export function eventPhrases(prev: DayStats | null, now: DayStats): Phrase[] {
  if (!prev) return [];
  const order: (keyof typeof EVENT)[] = ['shipped', 'receipts', 'picks', 'assembled'];
  for (const key of order) {
    const delta = (now as any)[key] - (prev as any)[key];
    if (delta > 0) {
      return EVENT[key](now, delta).map((text, i) => ({ id: `${key}.${i}`, text }));
    }
  }
  return [];
}

export const idlePhrases = (): Phrase[] => IDLE.map((text, i) => ({ id: `idle.${i}`, text }));

// ------------------------------------------------------------ состояние в браузере
const KEY = 'mpwms.mood';
export interface MoodState { on: boolean; date: string; count: number; lastAt: number; recent: string[] }
const today = () => new Date().toISOString().slice(0, 10);
const EMPTY: MoodState = { on: true, date: today(), count: 0, lastAt: 0, recent: [] };

export function loadMood(): MoodState {
  let s = EMPTY;
  try {
    const raw = localStorage.getItem(KEY);
    if (raw) s = { ...EMPTY, ...JSON.parse(raw) };
  } catch { /* приватный режим браузера */ }
  return s.date === today() ? s : { ...s, date: today(), count: 0 };
}

export function saveMood(s: MoodState) {
  try { localStorage.setItem(KEY, JSON.stringify({ ...s, recent: s.recent.slice(-60) })); } catch { /* приватный режим */ }
}

/** Можно ли сейчас показать: лимиты смены плюс «не лезть под руку» */
export function mayShow(s: MoodState, now: number, busy: boolean): boolean {
  return s.on && !busy && s.count < MAX_PER_DAY && now - s.lastAt >= MIN_GAP_MS;
}

/** Человек печатает или открыт диалог — сообщение подождёт следующей проверки.
 *  Тип описан вручную: файл читают и тесты сервера, где браузерных типов нет. */
export interface DocLike {
  activeElement: { tagName?: string; isContentEditable?: boolean } | null;
  querySelector(selector: string): unknown;
}
export function isBusy(doc: DocLike | undefined = (globalThis as any).document): boolean {
  if (!doc) return false;
  const el = doc.activeElement;
  if (el?.tagName && /^(INPUT|TEXTAREA|SELECT)$/.test(el.tagName)) return true;
  if (el?.isContentEditable) return true;
  return !!doc.querySelector('.mantine-Modal-content, .mantine-Drawer-content');
}

// Переключатель в шапке: тот же приём, что и у выбора склада
const listeners = new Set<() => void>();
let state = loadMood();
export const moodStore = {
  get: () => state,
  set(next: Partial<MoodState>) {
    state = { ...state, ...next };
    saveMood(state);
    listeners.forEach((l) => l());
  },
  subscribe(fn: () => void) {
    listeners.add(fn);
    return () => listeners.delete(fn);
  },
};
